import { encrypt } from '@/common/securityUtils';
import { Form, useForm } from '@/components/forms';
import Select from '@/components/forms/Select';
import { Spinner } from '@/components/Spinner';
import { Button } from '@/design-system/button';
import { useAttributes } from '@/hooks/useAttributes';
import { useDropdown } from '@/hooks/useDropdown';
import { useDynamicLocale } from '@/hoslog-intl';
import { TrashIcon } from '@/icons';
import { useFieldArray, useWatch } from 'react-hook-form';
import { z } from 'zod';
import useUpdateMainPageHost from '../hooks/useUpdateMainPageHost';

const manageRecommendationSchema = z.object({
  locationId: z.string().min(1, { message: 'Required' }),
  groupId: z.string().min(1, { message: 'Required' }),
  clubId: z.string().min(1, { message: 'Required' }),
  displayOrderId: z.string().min(1, { message: 'Required' }),
  hosts: z
    .array(
      z.object({
        hostId: z.string(),
      }),
    )
    .min(1, { message: 'At least one host is required' })
    .max(3, { message: 'Maximum 3 hosts are allowed' })
    .superRefine((hosts, ctx) => {
      if (!hosts[0] || !hosts[0].hostId || hosts[0].hostId.trim() === '') {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Host is required',
          path: [0, 'hostId'],
        });
      }
    }),
});

export const ManageMainPageHostForm = ({
  locationId,
  groupId,
  clubId,
  addedHosts,
  displayOrderId,
  recommendationId,
}: {
  locationId: string;
  groupId: string;
  clubId: string;
  addedHosts: {
    hostId: string;
  }[];
  displayOrderId: string;
  recommendationId: string;
}) => {
  const form = useForm({
    schema: manageRecommendationSchema,
    mode: 'onBlur',
    defaultValues: {
      locationId: locationId,
      groupId: groupId,
      clubId: clubId,
      hosts: addedHosts,
      displayOrderId: encrypt(displayOrderId),
    },
  });

  const { fields, append, remove } = useFieldArray({
    control: form.control,
    name: 'hosts',
  });

  const hostData = useWatch({
    control: form.control,
    name: 'hosts',
  });

  const storeId = useWatch({
    control: form.control,
    name: 'clubId',
  });

  const { t } = useDynamicLocale();
  const { data: locationData } = useAttributes('location');
  const { data: groupData, isLoading: groupLoading } = useDropdown('MainPage', locationId, undefined, 'Group');
  const { data: storeData, isLoading: storeLoading } = useDropdown('MainPage', locationId, undefined, 'Store');
  const { data: hostDropdownData, isLoading: hostLoading } = useDropdown('MainPage', storeId, undefined, 'Host');
  const { data: displayOrderOptions, isLoading: displayOrderLoading } = useDropdown('displayOrderId');

  const handleRemove = (index: number) => {
    if (fields.length === 1) {
      form.setValue(`hosts.${index}.hostId`, '');
      return;
    }
    remove(index);
  };

  const { mutateAsync } = useUpdateMainPageHost();
  const isLoading = groupLoading || storeLoading || hostLoading || displayOrderLoading;
  return isLoading ? (
    <Spinner />
  ) : (
    <Form
      form={form}
      onSubmit={async (data) => {
        const payload = {
          recommendationId,
          hosts: data.hosts.filter((h) => h.hostId && h.hostId.trim() !== ''),
        };
        await mutateAsync(payload);
      }}
      className="overflow-y-auto max-h-[553px] scrollbar-none px-0.5"
    >
      <div className="space-y-[18px]">
        <Select
          label="common.form.label.location"
          {...form.register('locationId')}
          placeholder={t('common.form.placeholder.location')}
          options={locationData ?? []}
          disabled
        />
        <div className="grid grid-cols-2 gap-[18px]">
          <Select
            label="common.form.label.group"
            {...form.register('groupId')}
            placeholder={t('common.form.placeholder.group')}
            options={groupData ?? []}
            disabled
          />
          <Select
            label="common.form.label.storeName"
            {...form.register('clubId')}
            placeholder={t('common.form.placeholder.storeName')}
            options={storeData ?? []}
            disabled
          />
        </div>
        <Select
          label="common.form.label.displayOrder"
          {...form.register('displayOrderId')}
          placeholder={t('common.form.placeholder.displayOrder')}
          options={displayOrderOptions ?? []}
          disabled
        />
        <div className="flex items-center gap-2">
          <div className="text-foreground text-[13px] font-bold leading-5">{t('common.form.label.host')}</div>
          <div className="h-px flex-1 bg-border-divider"></div>
        </div>
        {fields.map((field, index) => (
          <div key={field.id}>
            {(hostData?.[index]?.hostId || index !== 0) && (
              <div className="w-full flex justify-end">
                <Button
                  type="button"
                  variant={'ghost'}
                  onClick={() => handleRemove(index)}
                  className="p-0 hover:bg-card hover:text-foreground h-[16px] text-[14px] font-normal"
                >
                  <TrashIcon className="size-4 text-destructive" /> 削除
                </Button>
              </div>
            )}
            <div className="pb-3 border-b border-dotted border-b-border-divider grid gap-2">
              <Select
                label="common.form.label.host"
                {...form.register(`hosts.${index}.hostId` as const)}
                placeholder={t('common.form.placeholder.host')}
                options={
                  hostDropdownData?.filter(
                    (h) => !hostData.some((selected, i) => i !== index && selected.hostId === h.value),
                  ) ?? []
                }
                isRequired={index === 0 ? true : false}
              />
            </div>
          </div>
        ))}
        {fields.length < 3 && (
          <Button
            type="button"
            variant="outline"
            onClick={() => append({ hostId: '' })}
            className="bg-card border-dashed border-foreground text-[11px] pl-[15px] pr-[17px] py-2 h-[24px] hover:border-foreground hover:text-foreground"
          >
            {t('common.page.button.addNew')}
          </Button>
        )}
      </div>
    </Form>
  );
};
